import {
  Box,
  Switch,
  Input,
  VStack,
  HStack,
  Text,
  Spacer,
  Checkbox,
  Select,
} from '@chakra-ui/react'
import { FunctionComponent, useEffect, useState } from 'react'
import { colorLibrary } from '../../../../_mocks_/colors'
import { Shop, VariantType } from '../../../type/backendType'
import { IColor, IMesh } from '../../../type/modelType'
import { colorToMeshConfiguration } from '../../../utils/modelEngine/configuration'
import {
  deleteProductVariant,
  upsertProductVariant,
} from '../../../utils/modelEngine/product'

interface IProps {
  shop: Shop
  mesh: IMesh
  setMesh: React.Dispatch<React.SetStateAction<IMesh | null>>
}

const ColorConfig: FunctionComponent<IProps> = ({ shop, mesh, setMesh }) => {
  const [colors, setColors] = useState<IColor[]>(colorLibrary)
  const [allow, setAllow] = useState<boolean>(
    !!mesh.colors?.some(c => c.isOption)
  )

  const colorVariantId = shop.variants.edges.find(
    e => e.node.no === '3d-colors-library'
  )?.node.id

  useEffect(() => {
    if (mesh) {
      setAllow(!!mesh.colors?.some(c => c.isOption))
    }
  }, [mesh])

  return (
    <VStack spacing="4" alignItems="left">
      <Box>
        <Switch
          size="lg"
          colorScheme="brand"
          onChange={e => {
            setAllow(e.target.checked)
            if (!e.target.checked) {
              if (colorVariantId && mesh.productId) {
                deleteProductVariant(colorVariantId, mesh.productId)
              }

              const colorId = mesh.colors?.find(c => c.default)?.id
              if (colorId) {
                const color = colors.find(c => c.id === colorId)
                if (color) {
                  setMesh({ ...mesh, colors: [{ ...color, default: true }] })
                }
              }
            }
          }}
          isChecked={allow}
        />{' '}
        Allow user to change
      </Box>
      {allow && (
        <>
          <Input
            placeholder="Search colors"
            onChange={e => {
              const q = e.target.value
              const patt = new RegExp(`.*${q}.*`, 'i')
              setColors(colorLibrary.filter(c => patt.test(c.title)))
            }}
          />

          <VStack spacing="2">
            {colors.map(color => {
              const colorConfig =
                mesh.colors?.find(c => c.id === color.id) ?? color
              return (
                <HStack w="100%">
                  <HStack spacing="2">
                    <Box h="42px" w="42px" bg={color.hex}></Box>
                    <Text>
                      {color.title} ({color.hex})
                    </Text>
                  </HStack>

                  <Spacer />
                  <Switch
                    size="md"
                    colorScheme="brand"
                    isChecked={!!colorConfig.isOption}
                    onChange={e => {
                      const newMeshConfig = colorToMeshConfiguration(
                        { ...colorConfig, isOption: e.target.checked },
                        mesh
                      )

                      if (colorVariantId && mesh.productId) {
                        if (!newMeshConfig.colors?.some(c => c.isOption)) {
                          deleteProductVariant(colorVariantId, mesh.productId)
                        } else {
                          upsertProductVariant(
                            colorVariantId,
                            newMeshConfig.colors.filter(v => v.isOption),
                            mesh.productId
                          )
                        }
                      }

                      setMesh(newMeshConfig)
                    }}
                  />
                  <Checkbox
                    colorScheme="brand"
                    isChecked={!!colorConfig.default}
                    onChange={e => {
                      const newMeshConfig = colorToMeshConfiguration(
                        { ...colorConfig, default: e.target.checked },
                        mesh
                      )

                      setMesh(newMeshConfig)
                    }}
                  ></Checkbox>
                </HStack>
              )
            })}
          </VStack>
        </>
      )}

      {!allow && (
        <Select
          placeholder="Select Default Color"
          onChange={e => {
            const colorConfig = colors.find(
              c => c.id === e.target.value
            ) as IColor
            const newMeshConfig = colorToMeshConfiguration(
              { ...colorConfig, default: true },
              mesh
            )
            setMesh(newMeshConfig)
          }}
          value={mesh.colors?.find(c => c.default)?.id ?? ''}
        >
          {colors.map(color => (
            <option value={color.id}>{color.title}</option>
          ))}
        </Select>
      )}
    </VStack>
  )
}

export default ColorConfig
